import React, { useState } from 'react';
import { useProjectImages } from '../helper-tools/image-service';

// 🖼️ BEAUTIFUL PORTFOLIO GALLERY
export const ImageGallery = ({ 
  projectName,       
  columns = 3, 
  transform = 'optimized',
  maxImages = null,  // ← Control how many to show (null = show all 31)
  showCaptions = true,
  style = 'modern'   // 'modern', 'minimal', 'masonry'
}) => {
  const { images, loading, error } = useProjectImages(projectName, transform);
  const [selectedImage, setSelectedImage] = useState(null);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)',
        borderRadius: '16px',
        border: '1px solid var(--border-color)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--text-accent)',
            borderTop: '3px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ 
            color: 'var(--text-secondary)',
            fontSize: 'clamp(1rem, 1.5vw, 1.1rem)',
            margin: 0,
            fontWeight: '500'
          }}>
            Loading {projectName} portfolio...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '60px 20px',
        background: 'var(--bg-secondary)',
        borderRadius: '16px',
        border: '1px solid var(--border-color)'
      }}>
        <p style={{
          color: '#e74c3c',
          fontSize: 'clamp(1rem, 1.5vw, 1.1rem)',
          margin: 0
        }}>
          Unable to load {projectName} portfolio: {error}
        </p>
      </div>
    );
  }

  const displayImages = maxImages ? images.slice(0, maxImages) : images;

  // Grid configurations for different styles
  const gridStyles = {
    modern: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(${
        columns === 2 ? '300px' : 
        columns === 3 ? '280px' : 
        columns === 4 ? '220px' : '280px'
      }, 1fr))`,
      gap: 'clamp(20px, 3vw, 32px)',
      marginBottom: 'clamp(40px, 6vw, 60px)'
    },
    minimal: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(250px, 1fr))`,
      gap: 'clamp(16px, 2vw, 24px)',
      marginBottom: 'clamp(32px, 5vw, 48px)'
    },
    masonry: {
      columns: `${columns}`,
      columnGap: 'clamp(20px, 3vw, 32px)',
      marginBottom: 'clamp(40px, 6vw, 60px)'
    }
  };

  // Individual image styles
  const imageCardStyle = {
    borderRadius: 'clamp(12px, 2vw, 16px)',
    overflow: 'hidden',
    background: 'var(--bg-primary)',
    boxShadow: style === 'modern' 
      ? '0 4px 16px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.03)' 
      : '0 2px 8px rgba(0, 0, 0, 0.04)',
    border: '1px solid var(--border-color)',
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    cursor: 'pointer',
    position: 'relative',
    breakInside: style === 'masonry' ? 'avoid' : 'auto',
    marginBottom: style === 'masonry' ? 'clamp(20px, 3vw, 32px)' : '0'
  };

  return (
    <>
      <div style={gridStyles[style]}>
        {displayImages.map((img, index) => (
          <div 
            key={img.id} 
            style={imageCardStyle}
            onClick={() => setSelectedImage(img)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px) scale(1.01)';
              e.currentTarget.style.boxShadow = style === 'modern'
                ? '0 8px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)'
                : '0 4px 12px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = style === 'modern' 
                ? '0 4px 16px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.03)' 
                : '0 2px 8px rgba(0, 0, 0, 0.04)';
            }}
          >
            {/* Image Container */}
            <div style={{ 
              position: 'relative',
              overflow: 'hidden'
            }}>
              <img 
                src={img.src}
                alt={img.alt}
                style={{ 
                  width: '100%', 
                  height: style === 'masonry' ? 'auto' : '280px',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.03)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              />
              
              {/* Hover Overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.05) 100%)',
                opacity: 0,
                transition: 'opacity 0.4s ease-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = 0.8;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = 0;
              }}>
                <div style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-accent)',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid var(--border-color)'
                }}>
                  View Details
                </div>
              </div>
            </div>

            {/* Caption (if enabled) */}
            {showCaptions && (
              <div style={{ 
                padding: 'clamp(12px, 2vw, 16px)',
                background: 'var(--bg-primary)'
              }}>
                <h4 style={{
                  margin: '0 0 4px 0',
                  fontSize: 'clamp(0.9rem, 1.2vw, 1rem)',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  lineHeight: '1.3'
                }}>
                  {img.caption}
                </h4>
                <p style={{
                  margin: 0,
                  fontSize: 'clamp(0.8rem, 1vw, 0.875rem)',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.4'
                }}>
                  Portfolio piece {index + 1} of {displayImages.length}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox Modal (when image is clicked) */}
      {selectedImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <div style={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }}>
            <img 
              src={selectedImage.src}
              alt={selectedImage.alt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '8px',
                boxShadow: '0 20px 80px rgba(0, 0, 0, 0.3)'
              }}
            />
            <button
              onClick={() => setSelectedImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-accent)'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Add CSS keyframes for loading spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

// 🎯 SINGLE IMAGE COMPONENT - For individual images in your content
export const ProjectImageBlock = ({ 
  projectName,
  imageIndex = 0, 
  transform = 'optimized',
  style = 'modern',
  ...props 
}) => {
  const { images, loading } = useProjectImages(projectName, transform);
  
  if (loading || !images[imageIndex]) {
    return (
      <div style={{ 
        height: '300px', 
        background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)',
        borderRadius: 'clamp(12px, 2vw, 16px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-secondary)',
        marginBottom: 'clamp(24px, 4vw, 40px)',
        border: '1px solid var(--border-color)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '2px solid var(--text-accent)',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ margin: 0, fontSize: '1rem' }}>
            Loading {projectName} image...
          </p>
        </div>
      </div>
    );
  }

  const image = images[imageIndex];
  
  return (
    <div style={{ 
      maxWidth: '100%',
      marginBottom: 'clamp(24px, 4vw, 40px)',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      <div style={{
        borderRadius: 'clamp(12px, 2vw, 16px)',
        overflow: 'hidden',
        boxShadow: style === 'modern' 
          ? '0 4px 16px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.03)' 
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-primary)',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
      }}>
        <img 
          src={image.src}
          alt={image.alt}
          style={{ 
            width: '100%',
            height: 'auto',
            display: 'block',
            transition: 'transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.03)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        />
        {image.caption && (
          <div style={{ 
            padding: 'clamp(16px, 3vw, 20px)',
            background: 'var(--bg-primary)'
          }}>
            <p style={{ 
              fontSize: 'clamp(0.9rem, 1.3vw, 1rem)',
              color: 'var(--text-secondary)', 
              margin: 0,
              textAlign: 'center',
              fontStyle: 'italic',
              lineHeight: '1.4'
            }}>
              {image.caption}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};